import { useUser } from '@application/authentication/use_user';
import { Flex, Sheet } from '@cellula/react';
import { Auth } from '@feature/auth/auth.component';
import type { OpenUrlWithKind } from '@visiba/api-vcs-client-v2';
import { Timer } from '@visiba/std';
import { ReactNode, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface Props {
	url: string;
	requiresAuthentication: boolean;
	openWith: OpenUrlWithKind;
	sourceButtonId?: number;
	children?: ReactNode;
	className?: string;
}

export function Routeable({
	requiresAuthentication,
	url,
	children,
	openWith,
	sourceButtonId,
	className,
	...rest
}: Props): JSX.Element {
	// const parsedUrl = openWith === 'IFrame' ? convertUrlToEmbeddedWebLink(locationSelectorService.location.route, url) : url;
	const parsedUrl = addButtonIdToCareNavigationUrl(url, sourceButtonId);
	const user = useUser();

	// A not-yet-authenticated patient is forced to sign in before we navigate when the button is
	// configured to require authentication (the office "Kräv autentisering" / RequiresAuthentication
	// setting). For booking buttons this is enabled per-button in office the same way.
	// Only internal in-app navigation is gated here. External (http) links, new-tab links and iframe
	// content keep their existing behaviour; their RequiresAuthentication guard remains in
	// handleIntercept below.
	const rendersAsInternalLink = openWith !== 'IFrame' && isInternalUrl(url);
	const shouldForceLogin = user == null && requiresAuthentication && rendersAsInternalLink;

	if (shouldForceLogin) {
		return (
			<Auth.SignInSheet
				returnUrl={parsedUrl}
				trigger={
					<StyledTriggerButton type='button' className={className} {...rest}>
						{children}
					</StyledTriggerButton>
				}
			/>
		);
	}

	function handleIntercept(event: SyntheticEvent<HTMLAnchorElement>): void {
		// External / new-tab links that require authentication are blocked here. Internal links are
		// handled by the force-login branch above, so this only guards the non-internal cases.
		if (requiresAuthentication && user == null) {
			event.preventDefault();

			return;
		}
	}

	if (openWith === 'NewTab' && url.includes('http')) {
		return (
			<a href={parsedUrl} onClick={handleIntercept} target='_blank' rel='noreferrer' className={className} {...rest}>
				{children}
			</a>
		);
	}

	if (openWith === 'SameTab' && url.includes('http')) {
		return (
			<a href={parsedUrl} onClick={handleIntercept} className={className} {...rest}>
				{children}
			</a>
		);
	}

	if (openWith === 'IFrame') {
		return (
			<Sheet trigger={<button style={{ all: 'unset' }}>{children}</button>} variant='full-restricted'>
				<IframeContent url={parsedUrl} />
			</Sheet>
		);
	}

	return (
		<Link to={parsedUrl} onClick={handleIntercept} className={className} {...rest}>
			{children}
		</Link>
	);
}

function isInternalUrl(url: string): boolean {
	// Same-origin = internal in-app navigation. Resolving against the current origin makes
	// relative URLs internal while scheme-absolute (https://other) and protocol-relative
	// (//other) URLs resolve to a different origin and are treated as external.
	try {
		return new URL(url, window.location.origin).origin === window.location.origin;
	} catch {
		return false;
	}
}

const StyledTriggerButton = styled.button`
	all: unset;
	cursor: pointer;

	&:focus-visible {
		outline: revert;
	}
`;

function addButtonIdToCareNavigationUrl(url: string, buttonId: number | undefined): string {
	if (buttonId == null) {
		return url;
	}

	const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:/i.test(url);
	const parsedUrl = new URL(url, window.location.origin);
	const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
	if (!pathSegments.includes('navigation')) {
		return url;
	}

	parsedUrl.searchParams.set('buttonId', `${buttonId}`);

	return isAbsoluteUrl ? parsedUrl.toString() : `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
}

function IframeContent(props: { url: string }) {
	const ref = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<number>(0);

	useEffect(() => {
		return Timer.wait(() => {
			setHeight(ref.current?.clientHeight ?? 0);
		}, 250);
	}, []);

	return (
		<Flex
			ref={ref}
			grow={1}
			style={{
				height: '100%',
				width: '100%',
			}}
		>
			{height > 0 ? (
				<iframe
					style={{
						all: 'unset',
						height: height + 30,
						paddingTop: '5px',
					}}
					title='-'
					src={props.url}
				/>
			) : null}
		</Flex>
	);
}

function _convertUrlToEmbeddedWebLink(prefix: string, url: string): string {
	return `${prefix}/ew?url=${url}`;
}
